import { PRODUCT_NAME } from "../lib/config";
import { OPERATOR } from "./operator";

/** A paragraph, a bulleted list, or a table (first row is the header). */
export type Block = string | { list: string[] } | { table: string[][] };
export interface Section {
  heading: string;
  blocks: Block[];
}
export interface LegalDoc {
  title: string;
  summary: string;
  sections: Section[];
}

const P = PRODUCT_NAME;
const US = OPERATOR.name;
const EMAIL = OPERATOR.email;

// The suppliers that touch personal data, kept in one place so the privacy policy and the data
// processing agreement can't disagree. Update both (and tell customers 30 days ahead) on change.
const SUBPROCESSORS = [
  ["Supplier", "What they do", "Where"],
  ["Amazon Web Services (AWS)", "Sign-in: user accounts, password checks and sign-in emails (Amazon Cognito)", "Sweden (EU)"],
  ["Amazon Web Services (AWS)", "Off-site backup storage. Backups are encrypted by us before they leave our servers; AWS can't read them", "Sweden (EU)"],
  ["Tailscale Inc.", "Secure network connection between the internet and our servers. Traffic is encrypted end to end; Tailscale can't read it", "Canada and USA (encrypted transit only)"],
];

export const TERMS: LegalDoc = {
  title: "Terms of service",
  summary: `The agreement between ${US} and the recruitment agency using ${P}: what we provide, what it costs, what you're responsible for, and what happens if either of us wants to stop.`,
  sections: [
    {
      heading: "About these terms",
      blocks: [
        `${P} is provided by ${US}, ${OPERATOR.description}, of ${OPERATOR.address} ("we", "us"). These terms are an agreement between us and the business that signs up for ${P} ("you", "the customer"), usually a recruitment agency.`,
        "The person who creates the agency's account, or accepts an updated version of these terms, confirms that they're allowed to agree to them on the business's behalf.",
        `${P} is for businesses. It isn't offered to consumers, and you confirm you're using it for your trade or profession.`,
        "Our data processing agreement and privacy policy form part of these terms. If they conflict about personal data, the data processing agreement wins.",
      ],
    },
    {
      heading: "The service",
      blocks: [
        `${P} is online software for running a recruitment agency: tracking companies, contacts, jobs, candidates and placements, with related tools such as search, importing and data protection features. We call it "the service".`,
        "We improve the service over time and may add, change or remove features. If we remove something you rely on in a way that materially reduces the service, we'll tell you at least 30 days beforehand where we reasonably can.",
        "Features marked as beta or preview are provided as they are, may change or end, and aren't covered by any commitments in these terms beyond our duty of reasonable care.",
      ],
    },
    {
      heading: "Accounts and your team",
      blocks: [
        "New agencies are reviewed before they can use the service. We may decline an application without giving a reason.",
        "Owners of your account can invite colleagues, change their roles and deactivate them. You're responsible for everyone you give access to, for keeping your user list current, and for what happens under your account.",
        "Each login is for one person. Keep passwords secret, use a strong one, and turn on two-step verification where it's offered. Tell us straight away if you think an account has been misused.",
      ],
    },
    {
      heading: "Fees and payment",
      blocks: [
        "Unless we've agreed in writing that your agency is on a free plan, the service is paid by subscription:",
        {
          list: [
            "Monthly: £50 a month for your agency, which includes its first active user, plus £20 a month for each additional active user.",
            "Annual: the yearly price shown on the Billing page when you subscribe, for the same structure of an agency fee plus a fee per additional active user.",
          ],
        },
        "Subscriptions are paid in advance through our payment provider, Stripe, by card or Direct Debit, and renew automatically at the end of each period until cancelled.",
        "An active user is anyone in your agency whose account isn't deactivated. When someone joins, the extra fee is charged for the rest of the current period; when someone is deactivated, the unused part is credited against your next invoice.",
        "We aren't currently registered for VAT, so no VAT is charged. If we register, VAT will be added to fees from the date of registration and we'll tell you beforehand.",
        "We may change our prices by giving you at least 30 days' notice. A change applies from your next renewal after the notice period ends, and you can cancel before it takes effect.",
        "If a payment fails, the service keeps working for 14 days while we and Stripe retry it. After that, your account becomes read-only until payment is made: you can still see and export your data and handle data protection requests, but you can't add or change anything else.",
        "Fees aren't refundable, except for credits for removed users as described above, where we end the service for convenience (see below), or where the law requires.",
        "Free plans are given at our discretion. We may end a free plan, or convert it to a paid plan, by giving at least 30 days' notice; you can then subscribe or leave and take your data with you.",
      ],
    },
    {
      heading: "Your data",
      blocks: [
        `Everything you or your users put into ${P} ("your data") stays yours. You give us permission to host, copy, process and display your data only as needed to provide, secure and support the service and as our data processing agreement allows.`,
        "For personal data in your data, such as candidates' and contacts' details, you are the controller and we are your processor. The data processing agreement sets out our obligations.",
        "You're responsible for your data: that you have the right to hold and use it, that you have a lawful basis for processing personal data, that people are told how you use their information, and that it's accurate and kept no longer than necessary. The service includes retention reviews, export and erasure to help you with this.",
        "You can export your data at any time, including after cancelling, until it's deleted as described under \"Ending the agreement\".",
      ],
    },
    {
      heading: "The shared company directory",
      blocks: [
        `Company profiles in ${P} (a company's name, website, sector and similar business information) form a directory shared by all agencies. Your jobs, contacts, terms, candidates and notes about a company are private to your agency; the company profile itself is not.`,
        "When you add or edit a company profile, you agree that other agencies can see and build on that information, and you confirm it's accurate and not confidential. Don't put personal data or anything confidential in a shared company profile.",
        "We may correct, merge, lock or remove company profiles to keep the directory accurate.",
      ],
    },
    {
      heading: "Acceptable use",
      blocks: [
        "You must not, and must not let anyone else:",
        {
          list: [
            "use the service unlawfully, including to discriminate unlawfully against candidates or to process personal data without a lawful basis;",
            "upload malicious code, or anything that infringes someone else's rights or that you're not entitled to share;",
            "try to access other agencies' data, test or bypass our security, or overload the service (responsible reports of security problems to us are welcome);",
            "copy, scrape, reverse engineer, resell or provide the service to others, or use it to build a competing product;",
            "upload special category data (such as health, ethnicity or religion) or criminal records data, unless it's genuinely necessary for a role and you have a lawful basis and condition for processing it.",
          ],
        },
        "We may suspend access, after warning you where reasonably possible, if we reasonably believe these rules are being broken or the security of the service is at risk.",
      ],
    },
    {
      heading: "What we provide",
      blocks: [
        "We'll provide the service with reasonable skill and care, keep it secure using the measures described in our data processing agreement, and back up your data every night.",
        "We aim to keep the service available at all times but don't guarantee it will be uninterrupted or error-free. We may need to take it offline for maintenance, and we'll try to do so outside UK working hours.",
        `Support is by email at ${EMAIL}. We'll reply as soon as we reasonably can, normally within two working days.`,
        "Except as these terms say, the service is provided without any other promises, and terms implied by law are excluded to the extent the law allows.",
      ],
    },
    {
      heading: "Our intellectual property",
      blocks: [
        `We (or our licensors) own the service, its software, design and content, other than your data. You get the right to use the service under these terms while your subscription or free plan lasts; nothing else is transferred to you.`,
        "If you send us suggestions or feedback, we may use them freely and without any obligation to you.",
      ],
    },
    {
      heading: "Confidentiality",
      blocks: [
        "Each of us will keep the other's confidential information confidential and use it only for this agreement, except where it's already public, independently developed, received lawfully from someone else, or must be disclosed by law. Your data is your confidential information.",
      ],
    },
    {
      heading: "Liability",
      blocks: [
        "Nothing in these terms limits liability for death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or for anything else that can't be limited by law.",
        "Subject to that, neither of us is liable to the other for loss of profits, revenue, business, goodwill or anticipated savings, or for any indirect or consequential loss, however it arises.",
        "Subject to the first paragraph, each party's total liability arising from or in connection with this agreement in any 12-month period is limited to the greater of the fees you paid us in that period and £500.",
        "This limit doesn't apply to your obligation to pay fees, or to either party's liability under \"Your responsibility for your data\" below.",
      ],
    },
    {
      heading: "Your responsibility for your data",
      blocks: [
        "You'll compensate us for reasonable losses and costs we incur because of a claim by a third party (including a regulator) arising from your data, or your use of the service, breaking the law or these terms, except where the claim is caused by our own breach.",
      ],
    },
    {
      heading: "Ending the agreement",
      blocks: [
        "You can cancel at any time from the Billing page. Cancellation takes effect at the end of the period you've paid for, and the service continues until then.",
        "We can end the agreement by giving you 30 days' notice for any reason, in which case we'll refund any fees you've paid for the period after it ends. Either of us can end it immediately by notice if the other seriously breaks these terms and doesn't put it right within 14 days of being asked, or becomes insolvent.",
        "When the agreement ends, your account becomes read-only for 30 days so you can export your data. After that we delete your data from the live service, and it disappears from our backups as they expire, within a further 90 days. We may keep records we're legally required to keep, such as invoices.",
        "The sections on fees owed, your data, confidentiality, liability and general terms continue after the agreement ends.",
      ],
    },
    {
      heading: "Changes to these terms",
      blocks: [
        "We may update these terms. We'll tell account owners about material changes by email or in the service at least 30 days before they take effect, and ask an owner to accept the new version. If you don't agree, you can cancel before the change takes effect.",
      ],
    },
    {
      heading: "General",
      blocks: [
        {
          list: [
            "These terms, the data processing agreement and the privacy policy are the whole agreement between us about the service.",
            "Neither of us is responsible for delays or failures caused by events outside our reasonable control.",
            "You may not transfer this agreement without our consent. We may transfer it to a business that takes over the service, and will tell you if we do.",
            "If part of these terms is found unenforceable, the rest still applies. Not enforcing a right straight away doesn't mean giving it up.",
            "No one else has rights under these terms under the Contracts (Rights of Third Parties) Act 1999.",
            `Notices may be given by email: to us at ${EMAIL}, and to you at your account owners' email addresses.`,
            "These terms are governed by the law of England and Wales, and the courts of England and Wales have exclusive jurisdiction.",
          ],
        },
      ],
    },
  ],
};

export const PRIVACY: LegalDoc = {
  title: "Privacy policy",
  summary: `How ${US} uses personal information about the people who use ${P}, and what happens to the candidate and contact information that agencies keep in it.`,
  sections: [
    {
      heading: "Who we are",
      blocks: [
        `${P} is provided by ${US}, ${OPERATOR.description}, of ${OPERATOR.address}. We're registered with the Information Commissioner's Office (ICO) under number ${OPERATOR.icoNumber}.`,
        `For anything about this policy or your information, email ${EMAIL}.`,
      ],
    },
    {
      heading: "Our two roles",
      blocks: [
        `We're the controller of information about the people who use ${P} (agency owners and their colleagues) and about our customers' billing. This policy explains how we use it.`,
        `Recruitment agencies use ${P} to keep information about candidates and the people they deal with at client companies. For that information the agency is the controller and we're its processor: we hold and process it only on the agency's instructions, under a data processing agreement.`,
      ],
    },
    {
      heading: "If you're a candidate or a client contact",
      blocks: [
        `If an agency holds your details in ${P}, the agency decides how they're used and is responsible for telling you. To see, correct or delete your information, or to object to its use, please contact the agency directly. If you contact us instead, we'll pass your request to the agency and help it respond.`,
      ],
    },
    {
      heading: "What we collect about users",
      blocks: [
        {
          list: [
            "Account details: your name, work email address, your agency and your role in it.",
            "Sign-in details: your password (stored only as a secure hash by our sign-in provider, never by us), two-step verification settings, and records of sign-ins.",
            "Activity in the service: an audit record of changes made in your agency (what changed, when and by whom), which helps agencies meet their own accountability obligations.",
            "Technical and security logs: requests to the service (time, page or action, and result). We don't record full web addresses, which can contain private links.",
            "Billing details, if you're an agency owner who subscribes: billing name, email and address, and subscription history. Card and bank details are collected and held by Stripe, our payment provider; we never see them.",
            "Messages you send us, for example support requests.",
          ],
        },
        "We don't use advertising or analytics cookies, and we don't track you across other websites. The service stores sign-in tokens in your browser so you stay signed in; these are strictly necessary for the service to work.",
      ],
    },
    {
      heading: "How we use it, and our lawful basis",
      blocks: [
        {
          table: [
            ["Purpose", "Lawful basis"],
            ["Providing the service: accounts, sign-in, your agency's workspace, support", "Contract with your agency (and our legitimate interest in serving the people it gives access to)"],
            ["Billing and keeping accounting records", "Contract; legal obligation"],
            ["Reviewing new agencies before they can use the service", "Legitimate interests: protecting the service and other customers"],
            ["Security, preventing misuse, and fixing problems", "Legitimate interests: keeping the service and its data safe"],
            ["Telling you about changes to the service, these documents or your subscription", "Contract; legitimate interests"],
          ],
        },
        "We don't sell personal information, and we don't use it for automated decisions that significantly affect you.",
      ],
    },
    {
      heading: "Who we share it with",
      blocks: [
        "We use these suppliers, who process personal information on our behalf under contracts that require them to protect it:",
        { table: SUBPROCESSORS },
        "Stripe (Stripe Payments UK Ltd and its group) processes agency owners' payment and billing information to take subscription payments. Stripe acts partly as an independent controller, under its own privacy policy, for example to prevent fraud.",
        `Our servers are run by us in the United Kingdom, and that's where your agency's data is held. We may also share information with professional advisers, with a buyer if the business is sold (who would have to honour this policy), or where the law requires.`,
      ],
    },
    {
      heading: "International transfers",
      blocks: [
        "Sign-in and backup data is held in Sweden, which the UK recognises as providing adequate protection. Where a supplier handles information outside the UK and EU, such as Stripe, the transfer is protected by the UK's adequacy regulations or by the UK International Data Transfer Agreement or Addendum.",
      ],
    },
    {
      heading: "How long we keep it",
      blocks: [
        {
          list: [
            "Account details and audit records: for as long as your agency uses the service. When an agency leaves, its data is deleted 30 days after the agreement ends, and from backups within a further 90 days.",
            "Deactivated users: kept as part of the agency's records, so the history of who did what stays accurate, until the agency's data is deleted.",
            "Billing and accounting records: six years after the end of the financial year they relate to, as UK tax law requires.",
            "Technical logs: short periods, normally a few weeks at most.",
          ],
        },
      ],
    },
    {
      heading: "How we protect it",
      blocks: [
        "Connections are encrypted. Each agency's data is kept separate by the database itself, not just by the application. Files are reached only through short-lived signed links. Changes are recorded in an audit log, backups are taken nightly and tested regularly, and copies stored away from our servers are encrypted. Our data processing agreement describes these measures in more detail.",
      ],
    },
    {
      heading: "Your rights",
      blocks: [
        "You have the right to ask for a copy of your information, to have it corrected or deleted, to restrict or object to how we use it, and to receive it in a portable format. Some rights depend on the lawful basis we rely on and have exceptions.",
        `To use any of these rights, email ${EMAIL}. We'll reply within one month. There's normally no charge.`,
        "If you're unhappy with how we've handled your information, please tell us first so we can try to put it right. You can also complain to the Information Commissioner's Office at ico.org.uk or on 0303 123 1113.",
      ],
    },
    {
      heading: "Changes to this policy",
      blocks: [
        "We'll update this policy when how we use information changes, and show the version and date at the top. If changes are significant, we'll tell account owners by email or in the service.",
      ],
    },
  ],
};

export const DPA: LegalDoc = {
  title: "Data processing agreement",
  summary: `The terms under which ${US} processes personal data for the agencies that use ${P}, as Article 28 of the UK GDPR requires. It forms part of our terms of service.`,
  sections: [
    {
      heading: "Parties and roles",
      blocks: [
        `This agreement is between the customer named on the account ("the controller") and ${US}, ${OPERATOR.description} ("the processor"). It forms part of the terms of service and applies whenever the processor processes personal data for the controller in providing ${P}.`,
        'Terms such as "personal data", "processing", "controller", "processor", "data subject" and "personal data breach" have the meanings given in the UK GDPR and the Data Protection Act 2018 ("data protection law").',
      ],
    },
    {
      heading: "Details of the processing",
      blocks: [
        {
          table: [
            ["Item", "Detail"],
            ["Subject matter", `Hosting and operating ${P}, online software the controller uses to run its recruitment business.`],
            ["Duration", "The term of the terms of service, plus the period until the data is deleted as set out below."],
            ["Nature", "Storing, organising, searching (including reading text from uploaded CVs so they can be searched), displaying, exporting, backing up and erasing data, as directed through the service."],
            ["Purpose", "Providing the service to the controller and its users, keeping it secure, and supporting the controller."],
            ["Data subjects", "Candidates and prospective candidates; contacts at client and prospective client companies; the controller's own users."],
            ["Types of personal data", "Names and contact details; employment history, current role and employer; CVs and other documents; skills; location; salary expectations and notice periods; interview stages, feedback and notes; placement and fee details; records of activity and changes."],
            ["Special category data", "Not intended. The controller must not upload it unless necessary and lawful (see \"Controller's responsibilities\"). CVs may contain it incidentally."],
          ],
        },
      ],
    },
    {
      heading: "Processor's obligations",
      blocks: [
        "The processor will:",
        {
          list: [
            "process the personal data only on the controller's documented instructions, which are the terms of service, this agreement and the controller's use of the service, unless the law requires otherwise (in which case it will tell the controller first unless the law prohibits that). It will tell the controller if it believes an instruction breaks data protection law;",
            "make sure everyone authorised to process the data is bound by confidentiality;",
            "keep the data secure using appropriate technical and organisational measures, including those listed below, and keep them under review;",
            "help the controller, taking into account the nature of the processing, to respond to data subjects exercising their rights. The service lets the controller export everything held about a person and erase them without needing the processor's involvement;",
            "help the controller meet its obligations on security, breach notification, data protection impact assessments and prior consultation with the ICO, taking into account the information available to the processor;",
            "notify the controller without undue delay, and in any case within 48 hours, after becoming aware of a personal data breach affecting its data, with the information the controller needs to meet its own obligations, and keep it updated;",
            "make available the information needed to show it complies with this agreement, and allow audits by the controller or an auditor it appoints, on at least 30 days' notice, no more than once a year (unless following a breach or required by a regulator), during working hours and at the controller's cost. The auditor must be bound by confidentiality.",
          ],
        },
      ],
    },
    {
      heading: "Sub-processors",
      blocks: [
        "The controller gives general authorisation for the processor to use sub-processors. Those currently used are:",
        { table: SUBPROCESSORS },
        "The processor will give the controller at least 30 days' notice before adding or replacing a sub-processor, by email or in the service. If the controller objects on reasonable data protection grounds and the parties can't resolve it, the controller may end the terms of service and receive a refund of fees paid for the period after it ends.",
        "The processor will put a written contract in place with each sub-processor imposing data protection obligations equivalent to these, and remains liable to the controller for its sub-processors' performance.",
      ],
    },
    {
      heading: "International transfers",
      blocks: [
        "The controller's data is hosted in the United Kingdom. The processor won't transfer it outside the UK, other than as listed above, unless the destination is covered by UK adequacy regulations or the transfer is protected by appropriate safeguards under data protection law, such as the UK International Data Transfer Agreement or Addendum.",
      ],
    },
    {
      heading: "Security measures",
      blocks: [
        {
          list: [
            "Encryption of all connections to the service in transit.",
            "Separation of each customer's data enforced by the database itself (row-level security), with automated tests that check one customer can't reach another's data.",
            "Access within each customer's account limited by role, with sign-in through a dedicated identity provider and optional two-step verification.",
            "Uploaded files reachable only through short-lived signed links.",
            "An append-only audit log of changes, which is scrubbed of personal data when a person is erased.",
            "Nightly backups, checked by regular test restores, with copies encrypted before they're stored away from the primary servers.",
            "Automated monitoring of availability, errors, storage and backups, with alerts to the processor.",
            "Administrative access limited to the people who need it to run the service, and security updates applied promptly.",
            "Tools for the controller to meet its own obligations: retention reviews, export of everything held about a person, and erasure that removes their CVs and personal details.",
          ],
        },
      ],
    },
    {
      heading: "Controller's responsibilities",
      blocks: [
        "The controller is responsible for having a lawful basis (and, where needed, a condition) for the processing it instructs; for giving data subjects the information data protection law requires; for the accuracy of the data and keeping it no longer than necessary; and for making sure its instructions comply with data protection law. It must not upload special category or criminal offence data unless that's necessary and lawful.",
      ],
    },
    {
      heading: "Return and deletion",
      blocks: [
        "The controller can export its data at any time. When the terms of service end, the account stays available read-only for 30 days so the controller can export what it needs. The processor then deletes the controller's data from the live service, and it's removed from backups as they expire within a further 90 days, unless the law requires it to be kept.",
      ],
    },
    {
      heading: "Liability and precedence",
      blocks: [
        "Each party's liability under this agreement is subject to the limits in the terms of service. If this agreement conflicts with the terms of service about personal data, this agreement wins. It's governed by the law of England and Wales.",
      ],
    },
  ],
};

export const LEGAL_DOCS = { terms: TERMS, privacy: PRIVACY, dpa: DPA } as const;
export type LegalDocKey = keyof typeof LEGAL_DOCS;
